import { useCallback } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHandleVerifyCard } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Types & Schemas */
export const CardVerificationFormSchema = z.object({
  title: z
    .string({
      message: 'Title is required',
    })
    .min(1, { message: 'Title is required' }),
  cardNumber: z
    .string({
      message: 'Card number is required',
    })
    .min(1, { message: 'Card number is required' }),
  cardId: z
    .string({
      message: 'Card ID is required',
    })
    .refine((value) => !value || !isNaN(Number(value)), {
      message: 'Card Id must be a valid number',
    }),
  file: z.instanceof(File).refine((file) => file.size < 2000000, {
    message: 'File must be less than 2MB.',
  }),
});

export type CardVerificationFormType = z.infer<
  typeof CardVerificationFormSchema
>;

/* Internal components */
type CardVerificationFormProps = {
  handleVerifyCard: (
    title: string,
    cardNumber: string,
    cardId: number,
    file: File,
  ) => Promise<void>;
};

const CardVerificationForm = ({
  handleVerifyCard,
}: CardVerificationFormProps) => {
  const form = useForm<CardVerificationFormType>({
    resolver: zodResolver(CardVerificationFormSchema),
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: CardVerificationFormType) => {
      const { title, cardNumber, cardId, file } = data;

      try {
        await handleVerifyCard(title, cardNumber, Number(cardId), file);
        form.reset();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [form, handleVerifyCard],
  );

  /* Render */
  return (
    <Form {...form}>
      <form
        className='w-full flex flex-col gap-2'
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name='title'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{'Title'}</FormLabel>
              <FormControl>
                <Input {...field} placeholder='Title' type='text' />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='cardNumber'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{'Card Number'}</FormLabel>
              <FormControl>
                <Input {...field} placeholder='Card Number' type='number' />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='cardId'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{'Card ID'}</FormLabel>
              <FormControl>
                <Input {...field} placeholder='Card ID' type='number' />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='file'
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          render={({ field: { value, onChange, ...fieldProps } }) => (
            <FormItem>
              <FormLabel>Picture</FormLabel>
              <FormControl>
                <Input
                  {...fieldProps}
                  placeholder='Picture'
                  type='file'
                  accept='image/jpg, image/png'
                  onChange={(event) =>
                    onChange(event.target.files && event.target.files[0])
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className='w-full cursor-pointer uppercase'
          type='submit'
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'verifying...' : 'verify'}
        </Button>
      </form>
    </Form>
  );
};

/* Main component */
export const CardVerification = () => {
  const { handleVerifyCard } = useHandleVerifyCard();

  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <CardVerificationForm handleVerifyCard={handleVerifyCard} />
    </section>
  );
};
